import {
  DocumentTextIcon,
  MicrophoneIcon,
  PencilIcon,
  PhotoIcon,
  TrashIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { forwardRef, useState } from 'react';
import { Button, Loading } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { MediaType } from '../../../../../Chat.enum.ts';
import {
  formatDate,
  formatTime,
  parseSlides,
  scheduledTemplateFailureReason,
  scheduledTemplateOutcome,
} from '../../../../../Chat.functions.ts';
import { useChatContext } from '../../../../../Chat.hook.ts';
import {
  eScheduledTemplateOutcome,
  tAttachment,
  tChatEvent,
} from '../../../../../Chat.types.ts';
import useProgrammedMessageAction from '../../../../MessageBar/components/ProgrammedMessageAction/ProgrammedMessageAction.hook.ts';
import { findOutboundRefusal } from '../../../../../../../utils/ErrorDirectory.ts';
import './FollowUp.styles.scss';

const FollowUp = forwardRef<HTMLDivElement, tChatEvent>(function FollowUp(
  followUpEvent: tChatEvent,
  ref,
) {
  const { deleteFollowUp, loading } = useProgrammedMessageAction();
  const { selectedChat, setSelectedFollowUp } = useChatContext();
  // Only for the reason of a follow-up the server refused.
  const { t } = useTranslation('errors/errors');

  const [openLightbox, setOpenLightbox] = useState<boolean>(false);

  // BUG-741: this panel rendered every scheduled template as a plain "Mensaje
  // programado" with a live edit button, unlike FollowUpBubble in the chat
  // thread. The generic edit only rewrites this card's text via updateEvent — it
  // never touches the WBS campaign that actually sends — so an agent editing
  // here believes they changed the send when they changed nothing.
  const isWapiTemplate = followUpEvent.isWapiTemplate ?? false;
  const isConsumed = followUpEvent.followUp?.consumed ?? false;

  // Edit is meaningless for a template (there is nothing generic to edit) and
  // pointless once consumed.
  const canEdit = !isConsumed && !isWapiTemplate;

  // BUG-741: the card used to promise a future send unconditionally. It now
  // reports what the real WBS campaign actually did, once the reconciler knows.
  const outcome = scheduledTemplateOutcome(followUpEvent);

  // A follow-up that came due while the plan was paused is failed for good with
  // chat code 1016 (1017/1018 when it could not be tied to its workspace), and
  // its stored reason is the server's English refusal. Say it in the
  // customer's words instead.
  const refusal = findOutboundRefusal(followUpEvent.errors);
  const failureReason = refusal
    ? t(refusal.messageKey, { ns: 'errors/errors' })
    : scheduledTemplateFailureReason(followUpEvent);

  // Delete stays available for templates, and must also survive a terminal
  // FAILURE. Two reasons this is deliberately NOT folded into `canEdit`:
  //   1. FollowUpBubble's original single `!consumed && !isWapiTemplate` guard hid
  //      the trash as well as the pencil, which is why a scheduled template had no
  //      delete affordance in the chat thread at all — making this button the only
  //      way to cancel one anywhere in the product. "Mirroring the bubble" would
  //      have closed the last exit.
  //   2. The reconciler sets `consumed` on failure too (so the follow-up cron
  //      stops re-firing the card for a day), so gating purely on `!isConsumed`
  //      would render a failed template with no way to dismiss it.
  const canDelete = !isConsumed || outcome === eScheduledTemplateOutcome.FAILED;

  function onCloseLightbox() {
    setOpenLightbox(false);
  }

  const MediaIcon = ({ attachment }: { attachment: tAttachment }) =>
    ((attachment.mediaType === MediaType.IMAGE ||
      attachment.mediaType === MediaType.STICKER) && (
      <PhotoIcon className="w-5 text-primary" />
    )) ||
    (attachment.mediaType === MediaType.VIDEO && (
      <VideoCameraIcon className="w-5 text-primary" />
    )) ||
    (attachment.mediaType === MediaType.AUDIO && (
      <MicrophoneIcon className="w-5 text-primary" />
    )) ||
    (attachment.mediaType === MediaType.DOCUMENT && (
      <DocumentTextIcon className="w-5 text-primary" />
    )) ||
    null;

  return (
    <div
      ref={ref}
      tabIndex={0}
      className={`FollowUp ${followUpEvent.selectedIndex === followUpEvent.index && 'bg-primary/10'}`}
    >
      <div className="FollowUp__Header">
        <div>
          <h1 className="FollowUp__Header__Title">
            {isWapiTemplate ? 'Plantilla programada' : 'Mensaje programado'}
          </h1>
          {outcome === eScheduledTemplateOutcome.FAILED ? (
            <>
              <p className="FollowUp__Send text-error font-semibold">
                No se pudo enviar
              </p>
              <p className="FollowUp__Send text-error">
                {failureReason}
              </p>
            </>
          ) : outcome === eScheduledTemplateOutcome.SENT ? (
            <p className="FollowUp__Send">
              Enviada {formatDate(followUpEvent.followUp?.triggerDate ?? '')} a
              las {formatTime(followUpEvent.followUp?.triggerDate ?? '')}
            </p>
          ) : (
            <p className="FollowUp__Send">
              Se enviará {formatDate(followUpEvent.followUp?.triggerDate ?? '')}{' '}
              a las {formatTime(followUpEvent.followUp?.triggerDate ?? '')}
            </p>
          )}
        </div>
        {canEdit || canDelete ? (
          <div className="FollowUp__Header__Actions">
            {canEdit ? (
              <Button
                size="xs"
                color="ghost"
                shape="circle"
                onClick={() => {
                  setSelectedFollowUp({
                    id: followUpEvent.id,
                    trigger_date: new Date(
                      followUpEvent.followUp?.triggerDate || Date.now(),
                    ),
                    consumed: followUpEvent.followUp?.consumed || false,
                    // BUG-741: carry the template identity and the WBS campaign
                    // link into the edit selection. Nothing consumes them yet,
                    // but omitting them is what let the generic edit flow run on
                    // a template with a silently incomplete object.
                    isWapiTemplate,
                    campaignId: followUpEvent.followUp?.campaignId ?? null,
                    content: {
                      text: followUpEvent.content?.text || '',
                      attachments:
                        followUpEvent.content?.attachments || undefined,
                    },
                  });
                }}
              >
                <PencilIcon className="FollowUp__Header__Actions__Icon" />
              </Button>
            ) : null}

            {canDelete ? (
              <Button
                size="xs"
                color="ghost"
                shape="circle"
                onClick={() => deleteFollowUp(followUpEvent.id)}
              >
                {loading ? (
                  <Loading className="FollowUp__Header__Actions__Icon" />
                ) : (
                  <TrashIcon className="FollowUp__Header__Actions__Icon" />
                )}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
      <hr className="my-1 border-blue-400" />
      <p className="break-words">{followUpEvent.content?.text}</p>
      {followUpEvent.content?.attachments?.length ? (
        <div
          className={`flex gap-2 ${followUpEvent.content.attachments.length > 1 ? 'justify-end' : ''}`}
          onClick={() => setOpenLightbox(true)}
        >
          <div className="flex gap-2 items-center border rounded-badge w-9/12 py-[2px] px-4">
            <MediaIcon attachment={followUpEvent.content.attachments[0]} />
            <p className="truncate">
              {followUpEvent.content.attachments[0].filename ||
                `${followUpEvent.content.attachments[0].mimeType?.split('/')?.[1]} File`}
            </p>
          </div>

          {followUpEvent.content.attachments.length > 1 ? (
            <div className="text-sm border rounded-full w-8 h-8 flex justify-center items-center">
              <p>{`+${followUpEvent.content.attachments.length - 1}`}</p>
            </div>
          ) : null}

          {openLightbox && (
            <Lightbox
              plugins={[
                Zoom,
                Captions,
                Thumbnails,
                Download,
                Fullscreen,
                Video,
              ]}
              open={openLightbox}
              close={onCloseLightbox}
              slides={parseSlides(followUpEvent.content.attachments)}
              thumbnails={{ hidden: true }}
            />
          )}
        </div>
      ) : null}
      <div className="FollowUp__Bottom">
        <span className="FollowUp__Header__Title">
          {
            selectedChat?.lead?.users?.find(
              (user) => user.auth_id === followUpEvent.createdBy,
            )?.name
          }
        </span>{' '}
        | {formatDate(followUpEvent.createdAt)} |{' '}
        {formatTime(followUpEvent.createdAt)}
      </div>
    </div>
  );
});

export default FollowUp;
